import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Play } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

// Import client logos
import luxoraLogo from "@/assets/logos/luxora.png";
import techvibeLogo from "@/assets/logos/techvibe.png";
import greenleafLogo from "@/assets/logos/greenleaf.png";
import novacoreLogo from "@/assets/logos/novacore.png";
import bluepeakLogo from "@/assets/logos/bluepeak.png";
import synthetixLogo from "@/assets/logos/synthetix.png";
import velociaLogo from "@/assets/logos/velocia.png";
import quantixLogo from "@/assets/logos/quantix.png";

// Import AI logos
import deepseekLogo from "@/assets/logos/deepseek.svg";
import claudeLogo from "@/assets/logos/claude.svg";
import geminiLogo from "@/assets/logos/gemini-icon.png";
import chatgptLogo from "@/assets/logos/chatgpt-icon.webp";
import perplexityLogo from "@/assets/logos/perplexity-new.png";

const clients = [
  { name: "Luxora", logo: luxoraLogo },
  { name: "TechVibe", logo: techvibeLogo },
  { name: "GreenLeaf", logo: greenleafLogo },
  { name: "NovaCore", logo: novacoreLogo },
  { name: "BluePeak", logo: bluepeakLogo },
  { name: "Synthetix", logo: synthetixLogo },
  { name: "Velocia", logo: velociaLogo },
  { name: "Quantix", logo: quantixLogo },
];

const aiLogos = [
  { name: "ChatGPT", logo: chatgptLogo, scale: "scale-100" },
  { name: "Gemini", logo: geminiLogo, scale: "scale-110" },
  { name: "Claude", logo: claudeLogo, scale: "scale-90" },
  { name: "Perplexity", logo: perplexityLogo, scale: "scale-100" },
  { name: "DeepSeek", logo: deepseekLogo, scale: "scale-125" },
];

const Hero = () => {
  const { t, language } = useLanguage();
  
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-44 pb-8"
    >
      {/* Dynamic gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-sky-50/50" />
      
      {/* Animated mesh gradient overlay */}
      <div 
        className="absolute inset-0 opacity-60"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 20% 40%, rgba(56, 189, 248, 0.15), transparent),
            radial-gradient(ellipse 60% 40% at 80% 20%, rgba(139, 92, 246, 0.1), transparent),
            radial-gradient(ellipse 50% 60% at 60% 80%, rgba(16, 185, 129, 0.08), transparent),
            radial-gradient(ellipse 40% 40% at 30% 70%, rgba(245, 158, 11, 0.06), transparent)
          `
        }}
      />
      
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Primary sky blue orb - larger and more visible */}
        <div 
          className="absolute top-1/4 right-1/3 w-[800px] h-[800px] rounded-full blur-[200px]"
          style={{ 
            background: 'radial-gradient(circle, rgba(56, 189, 248, 0.3), transparent 60%)',
            animation: 'float 8s ease-in-out infinite',
          }}
        />
        
        {/* Purple/violet orb */}
        <div 
          className="absolute bottom-1/3 left-1/4 w-[600px] h-[600px] rounded-full blur-[150px]"
          style={{ 
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2), transparent 60%)',
            animation: 'float 10s ease-in-out infinite reverse',
          }}
        />
        
        {/* Center teal orb */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full blur-[180px]"
          style={{ 
            background: 'radial-gradient(circle, rgba(14, 165, 233, 0.15), transparent 60%)',
            animation: 'pulse 6s ease-in-out infinite',
          }}
        />
        
        {/* Top right accent - amber */}
        <div 
          className="absolute top-20 right-20 w-[400px] h-[400px] rounded-full blur-[120px]"
          style={{ 
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.12), transparent 60%)',
            animation: 'float 12s ease-in-out infinite',
          }}
        />
        
        {/* Bottom accent - emerald */}
        <div 
          className="absolute bottom-20 right-1/3 w-[350px] h-[350px] rounded-full blur-[100px]"
          style={{ 
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1), transparent 60%)',
            animation: 'float 9s ease-in-out infinite reverse',
          }}
        />
      </div>

      {/* Subtle grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzk0YTNiOCIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40" />
      
      {/* Floating particles - white */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => {
          const size = 2 + Math.random() * 4;
          const top = Math.random() * 100;
          const left = Math.random() * 100;
          const delay = Math.random() * 5;
          const duration = 3 + Math.random() * 4;
          
          return (
            <div
              key={`particle-${i}`}
              className="absolute rounded-full"
              style={{
                top: `${top}%`,
                left: `${left}%`,
                width: `${size}px`,
                height: `${size}px`,
                background: 'rgba(255, 255, 255, 0.7)',
                animation: `twinkle-star ${duration}s ease-in-out ${delay}s infinite`,
                boxShadow: `0 0 ${size * 3}px rgba(255, 255, 255, 0.5)`,
              }}
            />
          );
        })}
      </div>

      {/* Twinkling stars - more realistic with varied sizes and behaviors */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(40)].map((_, i) => {
          const size = 0.8 + Math.random() * 2.5;
          const top = Math.random() * 100;
          const left = Math.random() * 100;
          const delay = Math.random() * 4;
          const duration = 2 + Math.random() * 3;
          const isPulsing = i % 3 === 0;
          
          return (
            <div
              key={`star-${i}`}
              className="absolute"
              style={{
                top: `${top}%`,
                left: `${left}%`,
                width: `${size}px`,
                height: `${size}px`,
                background: 'white',
                borderRadius: '50%',
                boxShadow: `
                  0 0 ${size}px rgba(255, 255, 255, 0.9),
                  0 0 ${size * 2}px rgba(255, 255, 255, 0.6),
                  0 0 ${size * 4}px rgba(255, 255, 255, 0.3)
                `,
                animation: isPulsing 
                  ? `star-pulse ${duration}s ease-in-out ${delay}s infinite`
                  : `twinkle-star ${duration}s ease-in-out ${delay}s infinite`,
              }}
            />
          );
        })}
      </div>

      {/* Shooting stars - distributed across entire hero */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => {
          // Distribute across full height (10% to 85%)
          const top = 10 + (i * 10) + Math.random() * 8;
          const delay = i * 3.5 + Math.random() * 2;
          const duration = 1.8 + Math.random() * 0.6;
          const length = 120 + Math.random() * 80;
          
          return (
            <div
              key={`shooting-${i}`}
              className="absolute"
              style={{
                top: `${top}%`,
                left: '-15%',
                width: `${length}px`,
                height: '1.5px',
                background: `linear-gradient(90deg, 
                  transparent 0%, 
                  rgba(255,255,255,0.05) 5%,
                  rgba(255,255,255,0.15) 15%,
                  rgba(255,255,255,0.35) 35%,
                  rgba(255,255,255,0.6) 55%,
                  rgba(255,255,255,0.85) 75%,
                  white 95%,
                  white 100%
                )`,
                borderRadius: '1px',
                boxShadow: '0 0 4px rgba(255, 255, 255, 0.8), 0 0 10px rgba(255, 255, 255, 0.4), 0 0 20px rgba(200, 220, 255, 0.2)',
                animation: `shooting-star ${duration}s ease-out ${delay}s infinite`,
                opacity: 0,
                transform: 'rotate(-12deg)',
              }}
            >
              {/* Bright star head */}
              <div 
                className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full"
                style={{
                  background: 'radial-gradient(circle, white 0%, rgba(255,255,255,0.9) 20%, rgba(255,255,255,0.4) 50%, transparent 70%)',
                  boxShadow: '0 0 6px white, 0 0 12px rgba(255,255,255,0.7), 0 0 20px rgba(200, 220, 255, 0.4)',
                }}
              />
            </div>
          );
        })}
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-5xl mx-auto text-center space-y-6 fade-in">

          {/* Main headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-display font-bold leading-[1.1] text-slate-800">
            {language === "fr" ? (
              <>
                Devenez la réponse préférée des
                <br />
                <span className="bg-gradient-to-r from-sky-400 via-blue-500 to-sky-400 bg-clip-text text-transparent animate-gradient-shine">
                  IA génératives
                </span>
              </>
            ) : (
              <>
                Become the preferred answer of
                <br />
                <span className="bg-gradient-to-r from-sky-400 via-blue-500 to-sky-400 bg-clip-text text-transparent animate-gradient-shine">
                  generative AI
                </span>
              </>
            )}
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl lg:text-2xl text-slate-500 max-w-4xl mx-auto leading-relaxed">
            {language === "fr" ? (
              <>
                1 personne sur 2 consulte une IA générative avant d'acheter.
                <br />
                Le GEO (Generative Engine Optimization) transforme le parcours d'achat.
                <br />
                Convertissez les conversations IA en leads qualifiés.
              </>
            ) : (
              <>
                1 in 2 people consult generative AI before buying.
                <br />
                GEO (Generative Engine Optimization) is transforming the buying journey.
                <br />
                Turn AI conversations into qualified leads.
              </>
            )}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
            <Button
              onClick={() => scrollToSection("contact")}
              size="lg"
              className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-semibold px-8 py-6 text-base rounded-full shadow-lg shadow-sky-500/25 hover:shadow-sky-500/40 transition-all duration-300 hover:scale-105 hover:-translate-y-1 group"
            >
              {t("hero.cta.primary")}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => scrollToSection("approach")}
              size="lg"
              variant="outline"
              className="border-2 border-slate-300 hover:border-sky-400 bg-white/60 hover:bg-white/80 text-slate-700 hover:text-sky-500 font-semibold px-8 py-6 text-base rounded-full backdrop-blur-sm transition-all duration-300 hover:scale-105 group"
            >
              <Play className="mr-2 h-5 w-5 text-sky-500" />
              {t("hero.cta.secondary")}
            </Button>
          </div>

          {/* AI Logos - transparent floating above */}
          <div className="pt-10">
            <div className="flex items-center justify-center gap-6 lg:gap-10 mb-8">
              {aiLogos.map((ai, index) => (
                <div
                  key={index}
                  className="h-8 w-8 lg:h-11 lg:w-11 opacity-40 hover:opacity-70 transition-all duration-500 flex items-center justify-center"
                >
                  <img 
                    src={ai.logo} 
                    alt={`Logo ${ai.name} - Intelligence artificielle générative`}
                    className={`h-full w-full object-contain ${ai.scale}`}
                  />
                </div>
              ))}
            </div>
            
            {/* Trusted by clients */}
            <p className="text-center text-xs font-medium text-slate-400 uppercase tracking-widest mb-4 mt-16">
              {language === "fr" ? "Ils nous font confiance" : "Trusted by leading companies"}
            </p>
            
            {/* Auto-scrolling marquee */}
            <div className="relative overflow-hidden max-w-4xl mx-auto">
              <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-slate-50 to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-slate-50 to-transparent z-10" />
              
              <div className="flex animate-marquee-scroll">
                {[...clients, ...clients].map((client, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 h-8 w-24 mx-4 grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300"
                  >
                    <img 
                      src={client.logo} 
                      alt={`Logo ${client.name} - Client de Gogenia.ai`}
                      loading="lazy"
                      className="h-full w-full object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-100 via-slate-100/80 to-transparent" />
    </section>
  );
};

export default Hero;